<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMatterStore } from './stores/matterStore';
import { useSessionStore } from './stores/sessionStore';
import { useChatStore } from './stores/chatStore';
import { useMatterContextModalStore } from './stores/matterContextModalStore';
import { useDataLoadStore } from './stores/dataLoadStore';
import { useDataInventoryStore } from './stores/dataInventoryStore';
import { useGuardrailsStore } from './stores/guardrailsStore';
import { useRouter, useRoute } from 'vue-router';
import MatterContextModal from './components/matter-context/MatterContextModal.vue';
import DataLoadModal from './components/data-load/DataLoadModal.vue';
import GuardrailsModal from './components/guardrails/GuardrailsModal.vue';
import MatterContextDebugView from './components/debug/MatterContextDebugView.vue';
import ContextMenu, { type ContextMenuItem } from './components/common/ContextMenu.vue';
import UserMenu from './components/auth/UserMenu.vue';
import IdleLogoutWarning from './components/auth/IdleLogoutWarning.vue';
import { useIdleLogout } from './composables/useIdleLogout';
import { useAuthStore } from './stores/authStore';
import { identifyUser, resetAnalytics, captureEvent } from './analytics';
import unionIcon from './assets/union.svg';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

// The login splash is a full-bleed branded screen (its own background image),
// so the app chrome header is suppressed while on it.
const isLoginRoute = computed(() => route.name === 'login');
const matterStore = useMatterStore();
const sessionStore = useSessionStore();
const chatStore = useChatStore();
const matterContextModalStore = useMatterContextModalStore();
const dataLoadStore = useDataLoadStore();
const dataInventoryStore = useDataInventoryStore();
const guardrailsStore = useGuardrailsStore();
const authStore = useAuthStore();

// Tie analytics events to the signed-in user. The auth store sets `user`
// after /auth/me resolves (login, reload, or guard probe) and nulls it on
// logout, so this watcher is the single place that mirrors identity into
// analytics. Both helpers no-op when analytics is disabled for the
// deployment, so this is safe to run unconditionally.
watch(
    () => authStore.user,
    (user) => {
        if (user) identifyUser(user);
        else resetAnalytics();
    },
    { immediate: true },
);

// Analytics: record when a matter is opened (entry into the matter
// workspace). Fires on the transition to a non-null matter id — tile click,
// deep-link hydration, or create-flow selection — and not on the return to
// the picker (null). matter_id only; captureEvent no-ops when analytics is
// off, so this is safe to run unconditionally.
watch(
    () => matterStore.currentMatter?.RecordId ?? null,
    (matterId) => {
        if (matterId) captureEvent('matter_opened', { matter_id: matterId });
    },
);

// Idle auto-logout for real SSO users (exempts the dev bypass). Drives the
// <IdleLogoutWarning> dialog below.
const { warningVisible, secondsRemaining, stayActive, logoutNow } = useIdleLogout();

// When the active matter changes (tile click, route guard hydrating a
// deep link, or going home) drop the session list. clearAllSessions also
// nulls currentSession, which the next watcher picks up to clear chats —
// so the chat-clear cascades through here, regardless of whether the
// trigger was a matter change or just a session deselect.
watch(
    () => matterStore.currentMatter?.RecordId ?? null,
    () => {
        sessionStore.clearAllSessions();
        // The inventory panel is matter-scoped; drop it so it never lingers
        // with the previous matter's data when a new matter is opened.
        dataInventoryStore.close();
    },
);

// Onboarding gate: the first time a matter is opened without published
// matter context, force the Matter Context modal so the user is walked
// through providing it. We re-check on every matter entry (immediate
// covers cold deep links where the route guard hydrates currentMatter
// before this component's first watch tick) and only prompt on a
// definitive "not published" status — a failed/absent status response
// fails open so a transient error doesn't trap the user.
watch(
    () => matterStore.currentMatter?.RecordId ?? null,
    async (matterId) => {
        // Drop the previous matter's status so the lock overlay
        // (chatStore.matterContextRequired) fails open until the new
        // matter's true state loads — avoids briefly showing one matter's
        // lock state while another is opening.
        chatStore.matterContextStatus = null;
        if (!matterId) return;
        const status = await chatStore.fetchMatterContextStatus(matterId);
        // Bail if the user navigated away while the request was in flight.
        if (!status || matterStore.currentMatter?.RecordId !== matterId) return;
        if (status.is_published === true) return;
        // Already prompting for this exact matter — don't re-open and wipe
        // any in-progress review the user has started.
        if (
            matterContextModalStore.visible &&
            matterContextModalStore.targetMatterId === matterId
        ) {
            return;
        }
        // Automatic onboarding open (context not set yet) — plays the open
        // animation; explicit user opens (openMatterContext) do not.
        matterContextModalStore.open(matterId, { auto: true });
    },
    { immediate: true },
);

// Availability-map gate: after the matter context gate clears, check whether
// a data scan has been run. If not, auto-open the Data Load modal so the user
// is walked through it before interacting with the agent.
watch(
    () => matterStore.currentMatter?.RecordId ?? null,
    async (matterId) => {
        dataLoadStore.hasMap = null;
        if (!matterId) return;
        await dataLoadStore.fetchMapStatus(matterId);
    },
    { immediate: true },
);

// Clear chats whenever the active session goes away (either because the
// session was cleared directly, or because the matter change above
// nulled it). When the session changes from one id to another, ChatWindow
// already reloads — don't double-clear there or we'd flicker.
watch(
    () => sessionStore.currentSessionId,
    (newId) => {
        if (!newId) chatStore.clearChats();
    },
);

const goHome = () => {
    // The router guard clears the selected matter on any route without a
    // :matterId, so no explicit clearCurrentMatter is needed here.
    router.push({ name: 'matter-selection' });
};

const openMatterContext = () => {
    const matter = matterStore.currentMatter;
    if (matter) matterContextModalStore.open(matter.RecordId);
};

const openDataLoad = () => {
    const matter = matterStore.currentMatter;
    if (matter) dataLoadStore.open(matter.RecordId);
};

// Matter Data Inventory: a right-hand panel (rendered in MatterPage) that
// shrinks the content area rather than overlaying it. The header button
// toggles it for the current matter.
const toggleDataInventory = () => {
    const matter = matterStore.currentMatter;
    if (matter) dataInventoryStore.toggle(matter.RecordId);
};

// Guardrails is a global (app-wide) setting, so it needs no matter id.
const openGuardrails = () => {
    guardrailsStore.open();
};

const headerContextMenu = ref<{ x: number; y: number } | null>(null);

const onHeaderTitleContextMenu = (event: MouseEvent) => {
    if (!matterStore.isMatterSelected) return;
    // Hold ctrl/cmd to bypass us and get the native browser menu.
    if (event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    headerContextMenu.value = { x: event.clientX, y: event.clientY };
};

const closeHeaderContextMenu = () => {
    headerContextMenu.value = null;
};

const headerMenuItems = computed<ContextMenuItem[]>(() => {
    const matter = matterStore.currentMatter;
    if (!matter) return [];
    return [
        {
            label: t('app.typeManagement'),
            icon: 'tune',
            onSelect: () => router.push({ name: 'types', params: { matterId: matter.RecordId } }),
        },
        {
            label: t('app.dataLoadManagement'),
            icon: 'storage',
            onSelect: openDataLoad,
        },
        {
            label: t('app.guardrailsManagement'),
            icon: 'security',
            onSelect: openGuardrails,
        },
    ];
});
</script>

<template>
    <div id="app-container">
        <header v-if="!isLoginRoute" class="app-header">
            <span
                v-if="matterStore.isMatterSelected"
                class="material-icons app-header-icon-matter app-header-icon-clickable"
                @click="goHome"
                >all_inbox</span
            >
            <img v-else :src="unionIcon" class="app-header-icon" alt="" />
            <h1 class="app-header-title" @contextmenu="onHeaderTitleContextMenu">
                {{
                    matterStore.isMatterSelected
                        ? matterStore.currentMatter!.Name
                        : t('app.title')
                }}
            </h1>
            <div v-if="matterStore.isMatterSelected" class="app-header-actions">
                <button
                    type="button"
                    class="app-header-action-btn"
                    :title="t('app.matterContext')"
                    :aria-label="t('app.matterContext')"
                    @click="openMatterContext"
                >
                    <span class="material-icons-outlined">fact_check</span>
                </button>
                <button
                    type="button"
                    class="app-header-action-btn"
                    :class="{ 'app-header-action-btn-active': dataInventoryStore.isOpen }"
                    :title="t('app.dataInventory')"
                    :aria-label="t('app.dataInventory')"
                    :aria-pressed="dataInventoryStore.isOpen"
                    @click="toggleDataInventory"
                >
                    <span class="material-icons-outlined">dns</span>
                </button>
            </div>
            <UserMenu />
        </header>

        <router-view />

        <MatterContextModal />
        <DataLoadModal />
        <GuardrailsModal />

        <IdleLogoutWarning
            :visible="warningVisible"
            :seconds-remaining="secondsRemaining"
            @stay="stayActive"
            @logout-now="logoutNow"
        />

        <ContextMenu
            :position="headerContextMenu"
            :items="headerMenuItems"
            @close="closeHeaderContextMenu"
        />

        <div
            v-if="chatStore.showMatterContextDebug"
            class="det-debug-overlay det-debug-overlay-elevated"
            @click.self="chatStore.closeMatterContextDebug()"
        >
            <div class="det-debug-overlay-panel">
                <div class="det-debug-overlay-header">
                    <h2>{{ t('app.matterContextDebug') }}</h2>
                    <span
                        class="material-icons det-debug-overlay-close"
                        @click="chatStore.closeMatterContextDebug()"
                        >close</span
                    >
                </div>
                <div class="det-debug-overlay-body">
                    <div
                        v-if="chatStore.matterContextDebugLoading && !chatStore.matterContextDebug"
                        class="det-debug-loading"
                    >
                        <div class="loading-dots"><span></span><span></span><span></span></div>
                        <span>{{ t('app.loadingMatterContextDebug') }}</span>
                    </div>
                    <MatterContextDebugView v-else />
                </div>
            </div>
        </div>
    </div>
</template>
